/**
 * META1-06 (PR #352): AgentChatSidebar — chat persistente en sidebar/panel
 * del agente. NO es una pantalla aparte — vive dentro del dashboard del
 * agente y se puede minimizar/expandir.
 *
 * Diferente del FloatingChatButton (chatbot legacy):
 *   - Anchor lateral (sidebar) en lugar de floating button
 *   - Botón sidebar visible siempre en dashboard
 *   - ToolResult cards renderizan acciones del LLM
 *   - ConfirmActionDialog para acciones críticas
 *   - Memorias persistentes visibles en tab dedicada
 */
import React, { useEffect, useRef, useState } from 'react';
import { useAgentAssistant } from '../hooks/useAgentAssistant';
import { ToolResultCard } from './ToolResultCard';
import { ConfirmActionDialog } from './ConfirmActionDialog';

export interface AgentChatSidebarProps {
  defaultOpen?: boolean;
  /**
   * GH#2332 — cuánto bajar el panel para que NO quede debajo de los avisos fijos de arriba (modo demo
   * z-[120], prueba vencida z-50). Antes era `top-0` fijo y el aviso amarillo le tapaba la cabecera:
   * «Cerrar ×», «Memorias» y «Limpiar» no respondían. MainLayout le pasa el mismo cálculo que usa para su
   * cabecera (`desplazamientoPorAvisos`). Sin avisos vale `0px`.
   */
  topOffset?: string;
}

export const AgentChatSidebar: React.FC<AgentChatSidebarProps> = ({
  defaultOpen = false,
  topOffset = '0px',
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const [showMemories, setShowMemories] = useState(false);
  const [input, setInput] = useState('');
  const {
    messages,
    sending,
    pendingConfirmation,
    memories,
    send,
    confirmAction,
    cancelConfirmation,
    refreshMemories,
    clearConversation,
  } = useAgentAssistant();
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) refreshMemories();
  }, [open, refreshMemories]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send(input);
    setInput('');
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed right-4 top-1/2 z-30 -translate-y-1/2 rounded-l-lg bg-blue-600 px-2 py-4 text-white shadow-lg hover:bg-blue-700"
        aria-label="Abrir asistente"
      >
        <span className="block text-xs writing-mode-vertical-rl" style={{ writingMode: 'vertical-rl' }}>
          🤖 Asistente
        </span>
      </button>
    );
  }

  return (
    <>
      <aside
        role="complementary"
        aria-label="Asistente del agente"
        // GH#2332: `top` en línea y `bottom-0` (no `top-0 h-full`): con un `top` distinto de 0 el alto se
        // reparte solo y el campo de escribir de abajo no se sale de la ventana.
        style={{ top: topOffset }}
        className="fixed bottom-0 right-0 z-40 flex w-96 flex-col border-l border-gray-200 bg-white shadow-2xl"
      >
        <header className="flex items-center justify-between border-b border-gray-200 bg-blue-600 p-3 text-white">
          <div>
            <div className="text-sm font-semibold">Asistente del Agente</div>
            <div className="text-xs opacity-80">
              {sending ? 'Pensando...' : `${memories.length} memorias activas`}
            </div>
          </div>
          <div className="flex gap-1">
            <button
              type="button"
              onClick={() => setShowMemories((v) => !v)}
              className="rounded p-1 text-white hover:bg-white/20"
              aria-label="Ver memorias"
              title="Memorias"
            >
              🧠
            </button>
            <button
              type="button"
              onClick={clearConversation}
              className="rounded p-1 text-white hover:bg-white/20"
              aria-label="Limpiar conversación"
              title="Limpiar"
            >
              🗑️
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="rounded p-1 text-white hover:bg-white/20"
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
        </header>

        {showMemories && (
          <div className="max-h-48 overflow-y-auto border-b border-gray-200 bg-gray-50 p-3 text-xs">
            <div className="mb-2 font-semibold text-gray-700">
              Memorias persistentes ({memories.length})
            </div>
            {memories.length === 0 && (
              <div className="text-gray-500">Sin memorias. Pídeme "recuerda X" para guardar.</div>
            )}
            {memories.map((m) => (
              <div key={m.id} className="mb-1 rounded bg-white p-2">
                <span className="rounded bg-blue-100 px-1 text-blue-700">{m.category}</span>
                <div className="mt-1 text-gray-800">{m.content}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex-1 overflow-y-auto p-3">
          {messages.length === 0 && !sending && (
            <div className="text-center text-sm text-gray-500">
              <div className="mb-2 text-2xl">👋</div>
              <div>Hola, soy tu asistente. Puedo:</div>
              <ul className="mt-2 text-left text-xs">
                <li>• Agendar visitas y crear leads</li>
                <li>• Consultar tus comisiones</li>
                <li>• Reasignar leads (con confirmación)</li>
                <li>• Recordar notas importantes</li>
                <li>• Generar descripciones de propiedades</li>
              </ul>
            </div>
          )}
          {messages.map((m, i) => (
            <div
              key={i}
              className={`mb-3 ${m.role === 'user' ? 'text-right' : 'text-left'}`}
            >
              <div
                className={`inline-block max-w-[85%] rounded-lg px-3 py-2 text-sm ${
                  m.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}
              >
                {m.content}
              </div>
              {m.toolCalls?.map((tc, j) => (
                <ToolResultCard key={j} result={tc} />
              ))}
            </div>
          ))}
          {sending && (
            <div className="text-left text-sm text-gray-500">
              <span className="inline-block animate-pulse rounded bg-gray-100 px-3 py-2">...</span>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form
          onSubmit={handleSubmit}
          className="flex gap-2 border-t border-gray-200 p-3"
        >
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="¿En qué puedo ayudar?"
            disabled={sending || !!pendingConfirmation}
            className="flex-1 rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="submit"
            disabled={sending || !input.trim() || !!pendingConfirmation}
            className="rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          >
            {sending ? '...' : 'Enviar'}
          </button>
        </form>
      </aside>

      <ConfirmActionDialog
        open={!!pendingConfirmation}
        confirmation={pendingConfirmation}
        onConfirm={confirmAction}
        onCancel={cancelConfirmation}
      />
    </>
  );
};
